/**
 * CdmCard - Interactive conjunction data message card
 *
 * Displays a single CDM summary with key risk metrics:
 * - Object names (primary vs secondary)
 * - TCA (Time of Closest Approach)
 * - Miss distance
 * - Collision probability
 * - Emergency reportable indicator
 *
 * Clickable to select for maneuver planning workflow.
 * Retro mission control aesthetic: dark bg, amber/cyan accents, monospace.
 */

import type { CdmSummary } from '../../types/cdm'

interface CdmCardProps {
  cdm: CdmSummary
  onSelect: (cdmId: string) => void
  isSelected?: boolean
}

/**
 * Format collision probability as scientific notation with proper display.
 * Returns "N/A" for null values.
 */
function formatPc(pc: number | null | undefined): string {
  if (pc == null) return 'N/A'
  if (pc === 0) return '0'
  const [mantissa, exponent] = pc.toExponential(2).split('e')
  return `${mantissa} \u00d7 10${exponent}` // × symbol
}

/**
 * Format miss distance in meters.
 */
function formatMissDistance(distanceM: number | null | undefined): string {
  if (distanceM == null) return 'N/A'
  if (distanceM >= 1000) {
    return `${(distanceM / 1000).toFixed(2)} km`
  }
  return `${distanceM.toFixed(1)} m`
}

/**
 * Format TCA as short human-readable date.
 */
function formatTca(isoString: string): string {
  try {
    const date = new Date(isoString)
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZoneName: 'short',
    })
  } catch {
    return isoString
  }
}

/**
 * Get risk color based on collision probability.
 * Red for high risk, amber for medium, green for low.
 */
function getRiskColor(pc: number | null | undefined, emergency: boolean | undefined): string {
  if (emergency) return '#ff6666' // red
  if (pc == null) return '#ffcc66' // amber (unknown risk)
  if (pc >= 1e-3) return '#ff6666' // red
  if (pc >= 1e-5) return '#ffcc66' // amber
  return '#66cccc' // green/cyan
}

function CdmCard({ cdm, onSelect, isSelected = false }: CdmCardProps) {
  const riskColor = getRiskColor(cdm.collision_probability, cdm.emergency_reportable)
  const primaryLabel = cdm.sat1_name || String(cdm.sat1_norad_id || 'Unknown')
  const secondaryLabel = cdm.sat2_name || String(cdm.sat2_norad_id || 'Unknown')

  const handleClick = () => {
    onSelect(cdm.cdm_id)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      onSelect(cdm.cdm_id)
    }
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      aria-label={`Select CDM ${cdm.cdm_id}: ${primaryLabel} vs ${secondaryLabel}`}
      aria-pressed={isSelected}
      className={`
        w-full text-left font-mono rounded border p-3
        transition-all duration-150 cursor-pointer
        hover:bg-retro-bg/80
        ${isSelected
          ? 'border-retro-cyan bg-retro-bg shadow-[0_0_8px_rgba(102,204,204,0.2)]'
          : 'border-retro-border bg-retro-surface hover:border-retro-dim'
        }
      `}
    >
      {/* Top row: CDM ID + Emergency badge */}
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-bold tracking-wider" style={{ color: riskColor }}>
          {cdm.cdm_id}
        </span>
        {cdm.emergency_reportable && (
          <span
            className="text-xs px-1.5 py-0.5 rounded uppercase tracking-wider font-bold"
            style={{ backgroundColor: 'rgba(255,102,102,0.15)', color: '#ff6666', border: '1px solid #ff6666' }}
          >
            EMERGENCY
          </span>
        )}
      </div>

      {/* Objects */}
      <div className="flex items-center gap-2 mb-2 text-xs">
        <span className="text-retro-amber truncate">{primaryLabel}</span>
        <span className="text-retro-dim">vs</span>
        <span className="text-retro-muted truncate">{secondaryLabel}</span>
      </div>

      {/* Metrics row */}
      <div className="grid grid-cols-3 gap-2 text-xs">
        {/* TCA */}
        <div>
          <div className="text-retro-dim uppercase tracking-wider mb-0.5" style={{ fontSize: '0.6rem' }}>
            TCA
          </div>
          <div className="text-retro-text tabular-nums">
            {formatTca(cdm.tca)}
          </div>
        </div>

        {/* Miss Distance */}
        <div>
          <div className="text-retro-dim uppercase tracking-wider mb-0.5" style={{ fontSize: '0.6rem' }}>
            Miss Dist
          </div>
          <div className="text-retro-text tabular-nums">
            {formatMissDistance(cdm.miss_distance_m)}
          </div>
        </div>

        {/* Pc */}
        <div>
          <div className="text-retro-dim uppercase tracking-wider mb-0.5" style={{ fontSize: '0.6rem' }}>
            Pc
          </div>
          <div className="tabular-nums" style={{ color: riskColor }}>
            {formatPc(cdm.collision_probability)}
          </div>
        </div>
      </div>

      {/* Selection indicator */}
      {isSelected && (
        <div className="mt-2 pt-2 border-t border-retro-border">
          <span className="text-xs text-retro-cyan uppercase tracking-wider">
            Selected for maneuver planning
          </span>
        </div>
      )}
    </button>
  )
}

export default CdmCard
